import { ICourse } from './ICourse';
import { FILENAMES, PLATFORM_VERSION, STEPS } from './constants';
import { ICourseModule } from './ICourseModule';
import { WorkflowServiceV2 } from './WorkflowServiceV2';
import { WorkflowServiceV1 } from './WorkflowServiceV1';
import { IWorkflowAction } from './IWorkflowAction';
import { ICourseRoles } from './ICourseRoles';
import { IProvider } from './IProvider';

export class WorkflowService {

  public static getPlatformService(course: ICourse) {
    return course.coursePlatformVersion === PLATFORM_VERSION.V2 ? WorkflowServiceV2 : WorkflowServiceV1;
  }

  public static getCourseSteps(course: ICourse) {
    return WorkflowService.getPlatformService(course).getCourseSteps();
  }

  public static getLastModified(course: ICourse, module?: ICourseModule) {
    let modified;
    let modifiedBy;

    if (module?.files) {
      for (const file of module.files) {
        if (!modified || file.modified > modified) {
          modified = file.modified;
          modifiedBy = file.modifiedBy?.name;
        }
      }
    }

    if (course.commentSet && module?.id) {
      for (const id in course.commentSet) {
        const comment = course.commentSet[id];
        if (comment.moduleId === module.id && (!modified || comment.timestamp > modified)) {
          modified = comment.timestamp;
          modifiedBy = comment.username;
        }
      }
    }

    return { modified, modifiedBy };
  }

  public static getAssignToExpertAction(roles: ICourseRoles, course: ICourse, moduleId: string): IWorkflowAction | undefined {
    if (!course.expert && moduleId === STEPS.REVIEW && WorkflowService.isAssignToExpertEnabled(course, roles)) {
      return {
        id: 'assign-to-expert',
        title: 'Assign to External Reviewer...',
        tooltip: 'Assign this course to an External Reviewer'
      };
    }
    return undefined;
  }

  public static getActions(roles: ICourseRoles, course: ICourse, moduleId: string, modules?: ICourseModule[]): IWorkflowAction[] {

    const isCreated = modules?.some(x => x.id === moduleId && x.files.some(f => f.name === FILENAMES.RESULTS));

    const result: IWorkflowAction[] = [];

    const modifyAction = WorkflowService.getWorkflowModifyAction(roles, moduleId, course, isCreated);
    if (modifyAction) {
      result.push(modifyAction);
    }

    const reviewAction = WorkflowService.getWorkflowReviewAction(roles, moduleId, course, isCreated);
    if (reviewAction) {
      result.push(reviewAction);
    }

    const assignToExpertAction = WorkflowService.getAssignToExpertAction(roles, course, moduleId);
    if (assignToExpertAction) {
      result.push(assignToExpertAction);
    }

    return result;
  }


  public static getNextStatus(course: ICourse, moduleId: STEPS, action: string | undefined, results: { [key: string]: any }) {
    return WorkflowService.getPlatformService(course).getNextStatus(moduleId, action, results);
  }

  static getModuleIdFromStep(courseStep: string) {
    const split = courseStep?.split('|') ?? [];
    return split[0].trim();
  }

  static getStateFromCourseStep(courseStep?: string) {
    const split = courseStep?.split('|') ?? [];
    if (split.length == 2)
      return split[1].trim();
  }

  public static getCourseBadgeLabel = (course: ICourse) => {
    return course?.CourseStep ?? '';
  }

  static isCourseCompleted(courseStep?: string): boolean {
    return courseStep === STEPS.COMPLETED;
  }

  static isStepCompleted(course: ICourse, moduleId: string): boolean {
    const courseStatus = course.CourseStep;
    return !!courseStatus && WorkflowService.getPlatformService(course).isStepCompleted(moduleId, courseStatus);
  }

  public static getWorkflowModifyAction = (roles: ICourseRoles, moduleId: string, course: ICourse, isCreated?: boolean): IWorkflowAction | undefined => {
    return WorkflowService.getPlatformService(course).getWorkflowModifyAction(roles, moduleId, course, isCreated);
  }

  public static getWorkflowReviewAction = (roles: ICourseRoles, moduleId: string, course: ICourse, isCreated?: boolean): IWorkflowAction | undefined => {
    return WorkflowService.getPlatformService(course).getWorkflowReviewAction(roles, moduleId, course, isCreated);
  }

  public static getStepDescription(course: ICourse, roles: ICourseRoles, moduleId: string): string {
    return WorkflowService.getPlatformService(course).getStepDescription(course, roles, moduleId);
  }

  public static getDetailedStepDescription(course: ICourse, roles: ICourseRoles) {
    return WorkflowService.getPlatformService(course).getDetailedStepDescription(course, roles);
  }

  public static isAssignToExpertEnabled(course: ICourse, roles: ICourseRoles) {
    return WorkflowService.getPlatformService(course).isAssignToExpertEnabled(course, roles);
  }

  public static getNextModule(course: ICourse, moduleId: string | undefined) {
    return WorkflowService.getPlatformService(course).getNextModule(moduleId);
  }

  public static getResultSources(course: ICourse, module: ICourseModule, results_template: any, provider: IProvider) {
    return WorkflowService.getPlatformService(course).getResultSources(course, module, results_template, provider);
  }

  public static getFeedbackSources(course: ICourse, module: ICourseModule, results_template: any, provider: IProvider) {
    return WorkflowService.getPlatformService(course).getFeedbackSources(course, module, results_template, provider);
  }

  public static getDraftStatus(course: ICourse) {
    return WorkflowService.getPlatformService(course).getDraftStatus(course.CourseStep);
  }

  public static getCurrentDeadline(course: ICourse, hasBeenReviewed: boolean): Date | null | undefined {
    return WorkflowService.getPlatformService(course).getCurrentDeadline(course.CourseStep, course.deadlines, hasBeenReviewed);
  }

  public static getPrimaryContact(course?: ICourse) {
    const courseContact = {
      name: course?.CourseProviderUsers?.[0]?.name,
      email: course?.CourseProviderUsers?.[0]?.email,
    }
    const providerContact = {
      name: course?.provider?.ProviderContact,
      email: course?.provider?.ProviderEmail,
    };
    return course?.CourseProviderUsers?.length ? courseContact : providerContact;
  }

  public static getdefaultProviderUser(provider?: IProvider) {
    if (provider?.ProviderEmail) {
      return {
        id: '',
        email: provider.ProviderEmail,
        name: provider.ProviderContact || provider.ProviderName || "Course Provider"
      };
    }
  }

  public static getCourseProviderUsers(course: ICourse) {
    return (course.CourseProviderUsers ?? []).map(u => ({
      email: u.email,
      name: u.name || "Provider User"
    }));
  }

  public static getCourseStaffUsers(course: ICourse) {
    return (course.CourseStaffUsers ?? []).map(u => ({
      email: u.email,
      name: u.name || "Staff User"
    }));
  }

  public static getCourseReviewers(course: ICourse) {
    return course.expert ? [{
      email: course.expert.email,
      name: course.expert.name || course.expert.email || "External Reviewer"
    }] : [];
  }
}